import React from 'react';
import { AcceptBlock, inputBlock, LangBlock, ShowNotify, TextAreaEditor } from '../../global';
import { SystemShared } from '../../../../shared/system';
import { WebStorage } from '../../../storage';
import { AdminDataTable } from './admin.table';
import {
    AdminButton,
    AdminFormField,
    AdminFormGrid,
    AdminLangTabs,
    AdminPageWrap,
    AdminPreview,
    AdminSection,
} from './admin.ui';

type ChangeLogState = {
    version: string;
    date: string;
    ru: string;
    ua: string;
    en: string;
};

type Props = {
    changeLogs: { id: string; date: string }[];
    loadChangelogs: () => void;
};

export const AdminChangelog = ({ changeLogs, loadChangelogs }: Props) => {
    const [, , LangString] = LangBlock();
    const [changeLog, setChangeLog] = React.useState<ChangeLogState>({
        version: '',
        date: '',
        ru: '',
        ua: '',
        en: '',
    });
    const [changeLogPrev, setChangeLogPrev] = React.useState<'ru' | 'ua' | 'en'>('ru');

    const isValid =
        changeLog.version && changeLog.date && changeLog.en && changeLog.ru && changeLog.ua;

    const sortedLogs = SystemShared.sortArrayObjects(
        changeLogs.map((q) => {
            const v = q.id.replace('.json', '').split('.');

            if (v.length !== 3) {
                return { ...q, ids: 9999999999 };
            }

            const v1 = v.map((part) => parseInt(part));

            return {
                ...q,
                ids: v1[0] * 1000000 + v1[1] * 1000 + v1[2],
            };
        }),
        [{ id: 'ids', type: 'DESC' }],
    );

    return (
        <AdminPageWrap>
            <AdminSection title={LangString('AdminChangelogTitle')} subtitle={LangString('AdminChangelogSubtitle')}>
                <AdminFormGrid>
                    <AdminFormField label={LangString('AdminVersion')}>
                        {inputBlock(
                            'text',
                            changeLog.version,
                            (val) =>
                                setChangeLog((old) => ({ ...old, version: val.replace('v', '') })),
                            LangString('AdminVersion'),
                        )}
                    </AdminFormField>
                    <AdminFormField label={LangString('AdminDate')}>
                        {inputBlock(
                            'text',
                            changeLog.date,
                            (val) => setChangeLog((old) => ({ ...old, date: val })),
                            LangString('AdminDate'),
                        )}
                    </AdminFormField>
                </AdminFormGrid>

                <div className="admin-editor-layout">
                    <div>
                        {(['ru', 'ua', 'en'] as const).map((lan) => (
                            <TextAreaEditor
                                key={lan}
                                helps={[]}
                                name={LangString('AdminEditorText', lan)}
                                onChange={(val) => setChangeLog((old) => ({ ...old, [lan]: val }))}
                                value={changeLog[lan]}
                                onChangeTrigger="onchange"
                            />
                        ))}
                    </div>
                    <div>
                        <AdminLangTabs
                            langs={['ru', 'ua', 'en'] as const}
                            active={changeLogPrev}
                            onSelect={(lang) => setChangeLogPrev(lang)}
                        />
                        <AdminPreview html={changeLog[changeLogPrev]} />
                    </div>
                </div>

                <AdminButton
                    variant="primary"
                    block
                    disabled={!isValid}
                    onClick={(e) => {
                        e.preventDefault();

                        if (!isValid) {
                            return ShowNotify(LangString('AdminCreateChangelog'), LangString('AdminRequireAllParams'), 'error');
                        }

                        WebStorage.ApiRequestPost('/api/admin/changelog/create', {
                            record: SystemShared.toBinary(JSON.stringify(changeLog)),
                        }).then((res) => {
                            if (res.status) {
                                ShowNotify(LangString('AdminCreateChangelog'), LangString('AdminSuccess'), 'success');
                                loadChangelogs();
                            } else {
                                ShowNotify(LangString('AdminCreateChangelog'), res.error_message, 'error');
                            }
                        });
                    }}
                >
                    {LangString('AdminSaveChangelog')}
                </AdminButton>

                <AdminDataTable
                    data={sortedLogs}
                    rowKey={(row) => row.id}
                    emptyMessage={LangString('AdminNoChangelog')}
                    columns={[
                        {
                            key: 'version',
                            label: LangString('AdminVersion'),
                            primary: true,
                            render: (row) => row.id.replace('.json', ''),
                        },
                        {
                            key: 'date',
                            label: LangString('AdminDate'),
                            render: (row) => (row.date ? row.date : LangString('AdminUndefined')),
                        },
                        {
                            key: 'edit',
                            label: LangString('AdminEdit'),
                            render: (row) => (
                                <AdminButton
                                    variant="primary"
                                    size="sm"
                                    onClick={async (e) => {
                                        e.preventDefault();

                                        if (!(await AcceptBlock())) {
                                            return;
                                        }

                                        WebStorage.ApiRequest('/api/changelog', {
                                            version: row.id,
                                        }).then((res) => {
                                            if (res?.status) {
                                                setChangeLog(res.data);
                                            }
                                        });
                                    }}
                                >
                                    {LangString('AdminEdit')}
                                </AdminButton>
                            ),
                        },
                        {
                            key: 'remove',
                            label: LangString('AdminRemove'),
                            render: (row) => (
                                <AdminButton
                                    variant="danger"
                                    size="sm"
                                    onClick={async (e) => {
                                        e.preventDefault();

                                        if (!(await AcceptBlock())) {
                                            return;
                                        }

                                        WebStorage.ApiRequest('/api/admin/changelog/remove', {
                                            changelog: row.id,
                                        }).then((res) => {
                                            if (res.status) {
                                                loadChangelogs();
                                            }
                                        });
                                    }}
                                >
                                    {LangString('AdminRemove')}
                                </AdminButton>
                            ),
                        },
                    ]}
                />
            </AdminSection>
        </AdminPageWrap>
    );
};
